import entityDefaults from '../data/entityDefaults.json';
import { GAP_MOCK_DEFAULTS } from '../data/methodologyConstants';
import invoiceSample from '../data/invoiceSample.json';
import { aggregateFromInvoices, createGapMocks } from './aggregateFromInvoices';
import { composeMici, summaryFromTier, tierFromMici, trendFromScore } from './composeMici';
import { clamp } from './interpolate';

const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const REG_TYPE_TRADE_LICENCE = 'TL';

/** Mock UI score band so demo profiles land in a STRONG / low-PRIME range. */
export const MOCK_DISPLAY_SCORE_MIN = 700;
export const MOCK_DISPLAY_SCORE_MAX = 850;

/** Seeded integer in `[min, max]` (inclusive), stable for a given seed. */
export const seededIntInRange = (seed, min, max) => {
  let s = ((seed >>> 0) ^ 0xa5a5a5a5) >>> 0;
  s = (s * 1664525 + 1013904223) >>> 0;
  return min + (s % (max - min + 1));
};

/**
 * Remap the headline MICI into the mock display band and nudge component scores
 * so the card stays coherent with the displayed total.
 */
export const applyMockDisplayBand = (composed, seed, min = MOCK_DISPLAY_SCORE_MIN, max = MOCK_DISPLAY_SCORE_MAX) => {
  const miciScore = seededIntInRange(seed, min, max);
  const natural = Math.max(composed.miciScore, 1);
  const factor = miciScore / natural;
  const dimensions = composed.dimensions.map((row) => {
    const score = Math.round(clamp(row.score * factor, 40, 99));
    return {
      ...row,
      score,
      trend: trendFromScore(score),
    };
  });
  const tier = tierFromMici(miciScore);

  return {
    ...composed,
    miciScore,
    tier,
    summary: summaryFromTier(tier),
    dimensions,
    naturalMiciScore: composed.miciScore,
  };
};

const formatAed = (amount) => {
  if (amount >= 1_000_000) return `AED ${(amount / 1_000_000).toFixed(2)}M`;
  if (amount >= 1_000) return `AED ${(amount / 1_000).toFixed(0)}K`;
  return `AED ${Math.max(0, amount).toFixed(0)}`;
};

/** Trailing 12 calendar months ending at `asOf` (inclusive), e.g. AUG 2025 – JUL 2026 (12M). */
export const formatTrailing12MPeriod = (asOf = new Date()) => {
  const end = new Date(asOf.getFullYear(), asOf.getMonth(), 1);
  const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
  const startLabel = `${MONTHS_SHORT[start.getMonth()]} ${start.getFullYear()}`;
  const endLabel = `${MONTHS_SHORT[end.getMonth()]} ${end.getFullYear()}`;
  return `${startLabel} \u2013 ${endLabel} (12M)`;
};

/** Stable numeric seed from profile id (same profile → same mock jitter across refreshes). */
export const seedFromProfileId = (profileId, salt = 0) => {
  const str = String(profileId || 'MICI');
  let hash = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash ^ (salt >>> 0)) >>> 0;
};

const buildReportMeta = (seed) => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const suffix = (seed % 1_000_000).toString(36).toUpperCase().padStart(6, '0').slice(-6);
  const iso = now.toISOString().slice(0, 10);
  return {
    id: `MICI-${iso}-${suffix}`,
    date: dateStr,
    url: 'marmin.ai/score',
  };
};

const registrationTypeCode = (businessProfile) => {
  const raw = businessProfile?.scheme_agency_id;
  if (raw && typeof raw === 'object') return raw.code || null;
  return raw || null;
};

/**
 * Business profiles use scheme_agency_id:
 * - TL → Trade Licence (+ registration authority)
 * - any other code → Cabinet Decision
 * - missing → seed-based mock
 */
export const resolveRegistrationDetail = (businessProfile, seed = 0) => {
  const code = registrationTypeCode(businessProfile);
  const registrationNumber = businessProfile?.company_id || businessProfile?.registration_number || null;
  const authority = businessProfile?.authority_name || null;

  if (code === REG_TYPE_TRADE_LICENCE) {
    const number = registrationNumber || 'CN-1234567';
    const authorityLabel = authority || 'DED Dubai';
    return {
      label: 'Trade Licence',
      value: `${number} \u00b7 ${authorityLabel}`,
    };
  }

  if (code) {
    return {
      label: 'Cabinet Decision',
      value: registrationNumber || String(code),
    };
  }

  const useTradeLicence = seed % 2 === 0;
  if (useTradeLicence) {
    return {
      label: 'Trade Licence',
      value: entityDefaults.tradeLicence || 'CN-1234567 \u00b7 DED Dubai',
    };
  }

  return {
    label: 'Cabinet Decision',
    value: 'CD-2024-00891',
  };
};

export const resolveTrn = (businessProfile) =>
  businessProfile?.party_tax_scheme?.company_id ||
  businessProfile?.tax_identifier ||
  businessProfile?.trn ||
  entityDefaults.trn;

export const resolveTin = (businessProfile) =>
  businessProfile?.tin ||
  businessProfile?.endpoint_id ||
  businessProfile?.peppol_participant?.identifier ||
  entityDefaults.tin;

export const mergeEntityDetails = (businessProfile, summary, seed = 0) => {
  const registration = resolveRegistrationDetail(businessProfile, seed);

  return {
    TRN: resolveTrn(businessProfile),
    'TIN (Peppol)': resolveTin(businessProfile),
    [registration.label]: registration.value,
    'Trailing 12M Revenue': formatAed(summary.ttmRevenue),
    'Invoices (12M)': summary.invoiceCount.toLocaleString('en-US'),
    'Active Buyers': String(summary.activeBuyers),
    ASP: entityDefaults.asp,
  };
};

export const buildMiciReport = ({ invoices = invoiceSample, gapMocks, businessProfile = null, seed } = {}) => {
  const resolvedSeed = seed ?? seedFromProfileId(businessProfile?.profile_id || businessProfile?.id || 'default');
  const resolvedGaps = gapMocks ?? createGapMocks(resolvedSeed);
  const aggregates = aggregateFromInvoices(invoices, resolvedGaps);
  const composed = applyMockDisplayBand(composeMici(aggregates), resolvedSeed);

  const entityName =
    businessProfile?.legal_name || businessProfile?.business_name || businessProfile?.name || entityDefaults.name;

  return {
    report: buildReportMeta(resolvedSeed),
    entity: {
      name: entityName,
      period: formatTrailing12MPeriod(new Date()),
      score: composed.miciScore,
      maxScore: 900,
      rating: composed.tier,
      summary: composed.summary,
      details: mergeEntityDetails(businessProfile, aggregates.summary, resolvedSeed),
    },
    dimensions: composed.dimensions,
    meta: {
      composite: composed.composite,
      invoiceCount: aggregates.summary.invoiceCount,
      seed: resolvedSeed,
      naturalMiciScore: composed.naturalMiciScore,
      source: 'mock-frontend',
    },
  };
};

export { createGapMocks, invoiceSample, GAP_MOCK_DEFAULTS };
